import { requireConfirmedEnrollmentStatus } from "../../utils/enrollmentStatus";
import { apiClient } from '../client';

/**
 * Endpoints para chamadas à API Sponte via proxy do backend
 * NUNCA expõe tokens ou códigos de cliente
 */

/**
 * Obtém a unidade do usuário logado
 * @private
 */
const getUnit = () => {
  try {
    const storageData = sessionStorage.getItem('loggedUser');
    if (storageData) {
      const user = JSON.parse(storageData);
      return user?.unit || null;
    }
  } catch (error) {
    console.error('Erro ao obter unidade:', error);
  }
  return null;
};

/**
 * Busca todos os estudantes de um ano letivo
 */
export const getAllStudents = async (anoLetivo, situacaoID = 1) => {
  const { data } = await apiClient.post('/sponte/students/all', {
    anoLetivo,
    situacaoID,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca aluno pelo nome
 */
export const getStudentByName = async (name) => {
  const { data } = await apiClient.post('/sponte/students/by-name', {
    name,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca lista de alunos por nome (inclui inativos)
 */
export const searchStudentsByName = async (name) => {
  const { data } = await apiClient.post('/sponte/students/search', {
    name,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca dados completos do aluno pelo nome
 */
export const getStudentCompleteByName = async (name) => {
  const { data } = await apiClient.post('/sponte/students/complete-by-name', {
    name,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca responsáveis de um aluno
 */
export const getResponsables = async (idSponte, requestOptions = {}) => {
  const { data } = await apiClient.post('/sponte/responsables', {
    idSponte,
    unit: getUnit(),
  }, requestOptions);
  return data.data;
};

/**
 * Busca informações completas de um responsável
 */
export const getResponsableInfo = async (idSponte, selectedStudentId, requestOptions = {}) => {
  const { data } = await apiClient.post('/sponte/responsables/info', {
    idSponte,
    selectedStudentId,
    unit: getUnit(),
  }, requestOptions);
  return data.data;
};

/**
 * Busca todas as turmas de um ano letivo
 */
export const getAllClassrooms = async (anoLetivo) => {
  const { data } = await apiClient.post('/sponte/classrooms/all', {
    anoLetivo,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca estudantes de uma turma
 */
export const getStudentsByClassroom = async (TurmaID) => {
  const { data } = await apiClient.post('/sponte/classrooms/students', {
    TurmaID,
    unit: getUnit(),
  });
  return data.data;
};

export const getClassroomsByStudent = async (AlunoID) => {
  const { data } = await apiClient.post('/sponte/classrooms/by-id-sponte', {
    AlunoID,
    unit: getUnit(),
  });
  return Array.isArray(data.data) ? data.data : data.data ? [data.data] : [];
};

/**
 * Busca disciplinas de uma turma
 */
export const getSubjects = async (TurmaID) => {
  // Validar TurmaID
  if (!TurmaID || TurmaID === null || TurmaID === undefined || TurmaID === '') {
    throw new Error(`TurmaID inválido: ${TurmaID}`);
  }

  // Normalizar TurmaID para número
  const turmaIDNum = typeof TurmaID === 'string' ? parseInt(TurmaID, 10) : Number(TurmaID);
  if (isNaN(turmaIDNum) || turmaIDNum <= 0) {
    throw new Error(`TurmaID inválido: ${TurmaID} (convertido para ${turmaIDNum})`);
  }

  const { data } = await apiClient.post('/sponte/subjects', {
    TurmaID: turmaIDNum,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca notas parciais
 */
export const getPartialGrades = async (TurmaID, AlunoID, DisciplinaID, period) => {
  const { data } = await apiClient.post('/sponte/grades/partial', {
    TurmaID,
    AlunoID,
    DisciplinaID,
    period,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Define nota parcial
 */
export const setPartialGrades = async (grades, avaliacao, nota) => {
  const { data } = await apiClient.post('/sponte/grades/partial/set', {
    grades,
    avaliacao,
    nota,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Define nota final
 */
export const setFinalGrades = async (grades, nota) => {
  const { data } = await apiClient.post('/sponte/grades/final/set', {
    grades,
    nota,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca boletim por disciplina
 */
export const getGradesBySubject = async (AlunoID, TurmaID, DisciplinaID) => {
  const { data } = await apiClient.post('/sponte/grades/by-subject', {
    AlunoID,
    TurmaID,
    DisciplinaID,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca boletim por disciplina para múltiplos alunos (batch)
 * Otimizado para reduzir requisições e evitar rate limiting
 */
export const getGradesBySubjectBatch = async (AlunoIDs, TurmaID, DisciplinaID) => {
  try {
    const { data } = await apiClient.post('/sponte/grades/by-subject/batch', {
      AlunoIDs,
      TurmaID,
      DisciplinaID,
      unit: getUnit(),
    });
    return data;
  } catch (error) {
    // Log para debug
    console.error('[getGradesBySubjectBatch endpoint] Erro capturado:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      rateLimitExceeded: error.response?.data?.rateLimitExceeded
    });
    // Re-lançar o erro para ser tratado no componente
    throw error;
  }
};

/**
 * Busca aluno por ID
 */
export const getStudentByID = async (AlunoID) => {
  const { data } = await apiClient.post('/sponte/students/by-id', {
    AlunoID,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca dados completos do aluno por ID (para Censo)
 */
export const getStudentByIDCenso = async (AlunoID, requestOptions = {}) => {
  const { data } = await apiClient.post('/sponte/students/complete-by-id', {
    AlunoID,
    unit: getUnit(),
  }, requestOptions);
  return data.data;
};

/**
 * Busca dados completos de múltiplos alunos (batch)
 * Otimizado para reduzir requisições e evitar rate limiting
 */
export const getStudentByIDCensoBatch = async (AlunoIDs, forceRefresh = false) => {
  const { data } = await apiClient.post('/sponte/students/complete-by-id/batch', {
    AlunoIDs,
    forceRefresh,
    unit: getUnit(),
  });
  return data;
};

/**
 * Busca pré-matrículas
 */
export const getAllPreMatriculas = async (aulas_inicio) => {
  const { data } = await apiClient.post('/sponte/pre-matriculas', {
    aulas_inicio,
    unit: getUnit(),
  });
  return data.data;
};


/**
 * Atualiza dados do aluno
 */
export const setUpdateStudents = async (AlunoID, student, completeSummary, enrollmentContractStatus) => {
  const { data } = await apiClient.post('/sponte/students/update', {
    AlunoID,
    student,
    completeSummary,
    enrollmentContractStatus,
    unit: getUnit(),
  });
  return data.data;
};

export const setEnrollmentPaymentOption = async (AlunoID, paymentOption, options = {}) => {
  const { data } = await apiClient.post('/sponte/students/enrollment-payment', {
    AlunoID,
    paymentOption,
    schoolYear: options.schoolYear,
    enrollmentSnapshot: options.enrollmentSnapshot,
    unit: getUnit(),
  });
  return data.data;
};

export const setEnrollmentContractStatus = async (AlunoID, options = {}) => {
  const { data } = await apiClient.post('/sponte/students/enrollment-contract-status', {
    AlunoID,
    schoolYear: options.schoolYear,
    paymentOption: options.paymentOption,
    contractSigned: options.contractSigned,
    imageAuthorization: options.imageAuthorization,
    imageRestrictions: options.imageRestrictions,
    enrollmentSnapshot: options.enrollmentSnapshot,
    unit: getUnit(),
  });
  return requireConfirmedEnrollmentStatus(data.data);
};

export const getEnrollmentContractStatuses = async (schoolYear, studentIds = [], requestOptions = {}) => {
  const { data } = await apiClient.post('/sponte/students/enrollment-contract-statuses', {
    schoolYear,
    studentIds,
    unit: getUnit(),
  }, requestOptions);
  if (!Array.isArray(data.data)) throw new Error("Não foi possível carregar os status de matrícula.");
  return data.data;
};

/**
 * Atualiza dados do pai
 */
export const setUpdateFather = async (father) => {
  const { data } = await apiClient.post('/sponte/father/update', {
    father,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Atualiza dados da mãe
 */
export const setUpdateMother = async (mother) => {
  const { data } = await apiClient.post('/sponte/mother/update', {
    mother,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca todas as matrículas
 */
export const getAllMatriculas = async (DataInicio, SituacaoID, schoolYear) => {
  const { data } = await apiClient.post('/sponte/matriculas/all', {
    DataInicio,
    SituacaoID,
    schoolYear,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca responsável financeiro de múltiplos alunos (batch)
 * Otimizado para reduzir requisições e evitar rate limiting
 */
export const getResponsavelFinanceiroBatch = async (AlunoIDs, forceRefresh = false) => {
  const { data } = await apiClient.post('/sponte/responsavel-financeiro/batch', {
    AlunoIDs,
    forceRefresh,
    unit: getUnit(),
  });
  return data;
};

/**
 * Busca responsável financeiro
 */
export const getResponsavelFinanceiro = async (AlunoID) => {
  const { data } = await apiClient.post('/sponte/responsavel-financeiro', {
    AlunoID,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Busca financeiro por contrato
 */
export const getFinanceiroByContract = async (ContratoID) => {
  const { data } = await apiClient.post('/sponte/financeiro/contract', {
    ContratoID,
    unit: getUnit(),
  });
  return data.data;
};

export const getStudentOpenInstallments = async (AlunoID, requestOptions = {}) => {
  const { data } = await apiClient.post('/sponte/students/open-installments', {
    AlunoID,
    unit: getUnit(),
  }, { timeout: 40000, ...requestOptions });
  return data.data;
};

/**
 * Busca financeiro por múltiplos contratos (batch com rate limiting)
 * @param {Array<number|string>} ContratoIDs - Array de IDs de contratos
 * @returns {Promise<Object>} Objeto com ContratoID como chave e dados financeiros como valor
 */
export const getFinanceiroByContractsBatch = async (ContratoIDs, forceRefresh = false) => {
  const { data } = await apiClient.post('/sponte/financeiro/contracts/batch', {
    ContratoIDs,
    forceRefresh,
    unit: getUnit(),
  });
  return data.data;
};

/**
 * Atualiza situação de múltiplos contratos em batch (processamento em lotes)
 * @param {Array<number|string>} ContratoIDs - Array de IDs de contratos
 * @param {number} SituacaoID - ID da situação (1=Vigente, 6=Pré-Matrícula, etc.)
 * @returns {Promise<Object>} Objeto com resultados e estatísticas
 */
export const setAllContractsBatch = async (ContratoIDs, SituacaoID) => {
  const { data } = await apiClient.post('/sponte/contracts/update/batch', {
    ContratoIDs,
    SituacaoID,
    unit: getUnit(),
  });
  return data;
};

/**
 * Busca estudantes e responsáveis por turma
 */
export const getStudentsAndResponsablesByClassroom = async (classromsList) => {
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const studentsData = [];

  // Importar normalizador dinamicamente para evitar dependência circular
  const { normalizeStudentsByClassroom } = await import('../../utils/sponteDataNormalizer');

  // Obtém os dados de alunos com delay entre as chamadas
  for (const TurmaID of classromsList) {
    const data = await getStudentsByClassroom(TurmaID);
    studentsData.push(data);
    await delay(500); // Delay de 500ms
  }

  // Normalizar todos os dados e extrair IDs dos alunos
  const studentsIds = studentsData
    .map((item) => {
      const normalized = normalizeStudentsByClassroom(item);
      return normalized.map((student) => student.AlunoID);
    })
    .filter(ids => ids.length > 0);

  const students = [].concat(...studentsIds);

  const responsablesData = [];

  // Obtém os dados dos responsáveis com delay entre as chamadas
  for (const student of students) {
    const data = await getResponsables(student);
    responsablesData.push(data);
    await delay(10000); // Delay de 10s
  }

  const responsablesIds = responsablesData.map((item) => {
    if (Array.isArray(item)) {
      return item.map((responsable) => responsable.ResponsavelID);
    } else {
      return item.ResponsavelID;
    }
  });
  const responsables = Array.from(new Set([].concat(...responsablesIds)));

  return Array.from(new Set([...students, ...responsables]));
};

/** Lista leve exclusiva do requerimento; erros permanecem visíveis ao chamador. */
export const getEnrollmentStudentDirectory = async (anoLetivo, situacaoID, requestOptions = {}, forceRefresh = false) => {
  const { data } = await apiClient.post('/sponte/students/all', {
    anoLetivo, situacaoID, unit: getUnit(), compact: true, forceRefresh,
  }, { timeout: 40000, ...requestOptions });
  return data.data;
};
